import { useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { money, fmtDate, fmtTime12 } from "@/lib/format";
import { toast } from "sonner";
import { Scissors, ArrowLeft, Star, Gift, Loader2, CreditCard, Award, CalendarClock, XCircle, CheckCircle2, Clock } from "lucide-react";

export default function Wallet() {
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [stage, setStage] = useState("phone"); // phone | code | card
  const [loading, setLoading] = useState(false);
  const [sentTo, setSentTo] = useState("");
  const [data, setData] = useState(null);

  const requestCode = async () => {
    setLoading(true);
    try { const r = await api.post("/public/wallet/request-code", { phone }); setSentTo(r.data.sent_to); setStage("code"); toast.success("Código enviado a tu correo"); }
    catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };
  const verify = async () => {
    setLoading(true);
    try { const r = await api.post("/public/wallet/verify", { phone, code }); setData(r.data); setStage("card"); }
    catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };

  const cancelAppt = async (id) => {
    if (!window.confirm("¿Seguro que deseas cancelar esta cita?")) return;
    try {
      await api.post(`/public/appointment/${id}/cancel`);
      toast.success("Cita cancelada");
      setData((d) => ({ ...d, upcoming: d.upcoming.filter((u) => u.id !== id) }));
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="min-h-screen bg-[#0f0f0f] text-[#f5f5f5]">
      <header className="border-b border-[#2a2a2a] h-16 flex items-center px-5">
        <Link to="/" className="flex items-center gap-2 text-[#a3a3a3]"><ArrowLeft className="w-4 h-4" /><Scissors className="w-5 h-5 text-[#d4af37]" /><span className="font-bold">NAKAMA</span></Link>
      </header>
      <div className="max-w-md mx-auto px-5 py-10">
        {stage !== "card" && (
          <>
            <Award className="w-12 h-12 text-[#d4af37] mb-4" />
            <h1 className="text-2xl font-extrabold mb-1">Programa de lealtad</h1>
            <p className="text-[#a3a3a3] text-sm mb-6">Consulta tus puntos y tu historial ingresando tu teléfono. Te enviaremos un código a tu correo.</p>
          </>
        )}

        {stage === "phone" && (
          <div className="space-y-4">
            <input className="nk-input" placeholder="Tu teléfono (10 dígitos)" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="wallet-phone" />
            <button onClick={requestCode} disabled={loading || phone.length < 10} className="nk-btn w-full flex items-center justify-center gap-2" data-testid="wallet-request">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}Enviar código
            </button>
          </div>
        )}

        {stage === "code" && (
          <div className="space-y-4">
            <p className="text-sm text-[#737373]">Enviamos un código de 4 dígitos a {sentTo}</p>
            <input className="nk-input text-center text-2xl tracking-[0.5em]" maxLength={4} placeholder="0000" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} data-testid="wallet-code" />
            <button onClick={verify} disabled={loading || code.length !== 4} className="nk-btn w-full flex items-center justify-center gap-2" data-testid="wallet-verify">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}Ver mi tarjeta
            </button>
            <button onClick={() => setStage("phone")} className="text-[#737373] text-sm w-full">Cambiar teléfono</button>
          </div>
        )}

        {stage === "card" && data && (
          <div className="space-y-6" data-testid="wallet-card">
            <div className="rounded-[16px] p-6 bg-gradient-to-br from-[#d4af37] to-[#8a6d1a] text-black relative overflow-hidden">
              <CreditCard className="absolute -right-4 -top-4 w-28 h-28 opacity-20" />
              <p className="text-xs uppercase tracking-widest font-semibold">Barbería Nakama</p>
              <p className="text-lg font-extrabold mt-6">{data.client.name}</p>
              <div className="flex items-end justify-between mt-4">
                <div><p className="text-xs opacity-70">Puntos</p><p className="text-3xl font-extrabold">{data.client.points}</p></div>
                <div className="text-right"><p className="text-xs opacity-70">Visitas</p><p className="text-xl font-bold">{data.client.total_visits}</p></div>
              </div>
              {data.client.is_vip && <span className="absolute top-4 right-4 bg-black text-[#d4af37] text-[10px] font-bold px-2 py-1 rounded-full flex items-center gap-1"><Star className="w-3 h-3 fill-[#d4af37]" />VIP</span>}
            </div>

            {data.reward && data.reward.threshold > 0 && (
              <div className="nk-card p-5" data-testid="wallet-freecut">
                {data.reward.available >= 1 ? (
                  <div className="flex items-center gap-3">
                    <Gift className="w-8 h-8 text-[#d4af37] shrink-0" />
                    <div>
                      <p className="font-bold text-[#d4af37]">¡Tienes {data.reward.available} corte{data.reward.available > 1 ? "s" : ""} gratis!</p>
                      <p className="text-xs text-[#a3a3a3]">Pídelo en tu próxima visita. Recibirás un código por correo para confirmarlo.</p>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-bold flex items-center gap-2"><Scissors className="w-4 h-4 text-[#d4af37]" />Corte gratis</p>
                      <p className="text-xs text-[#a3a3a3]">{data.reward.progress}/{data.reward.threshold}</p>
                    </div>
                    <div className="h-2.5 rounded-full bg-[#242424] overflow-hidden">
                      <div className="h-full bg-[#d4af37] rounded-full transition-all" style={{ width: `${Math.min(100, (data.reward.progress / data.reward.threshold) * 100)}%` }} />
                    </div>
                    <p className="text-xs text-[#a3a3a3] mt-2">Te {data.reward.threshold - data.reward.progress === 1 ? "falta" : "faltan"} <strong className="text-[#d4af37]">{data.reward.threshold - data.reward.progress}</strong> corte{data.reward.threshold - data.reward.progress === 1 ? "" : "s"} para tu corte gratis. 💈</p>
                  </>
                )}
              </div>
            )}

            {data.upcoming.length > 0 && (
              <div>
                <h3 className="font-bold mb-2">Próximas citas</h3>
                {data.upcoming.map((u) => (
                  <div key={u.id} className="nk-card p-4 mb-2" data-testid={`wallet-appt-${u.id}`}>
                    <div className="flex justify-between text-sm">
                      <span>{u.service_name} · {u.barber_name}</span>
                      <span className="text-[#d4af37]">{fmtDate(u.start)} {fmtTime12(u.start)}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mt-3">
                      <Link to={`/reschedule/${u.id}`} className="nk-btn-ghost flex items-center justify-center gap-2 py-2 text-sm" data-testid={`wallet-reschedule-${u.id}`}><CalendarClock className="w-4 h-4" />Reagendar</Link>
                      <button onClick={() => cancelAppt(u.id)} className="nk-btn-ghost flex items-center justify-center gap-2 py-2 text-sm text-[#ef4444] border-[#ef4444]/30" data-testid={`wallet-cancel-${u.id}`}><XCircle className="w-4 h-4" />Cancelar</button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {(data.past_appointments || []).length > 0 && (
              <div>
                <h3 className="font-bold mb-2">Citas pasadas</h3>
                {data.past_appointments.map((p) => {
                  const badge = {
                    attended: { label: "Asistió", cls: "bg-[#16a34a]/15 text-[#16a34a]", Icon: CheckCircle2 },
                    no_show: { label: "No asistió", cls: "bg-[#ef4444]/15 text-[#ef4444]", Icon: XCircle },
                    unconfirmed: { label: "Sin confirmar", cls: "bg-[#a3a3a3]/15 text-[#a3a3a3]", Icon: Clock },
                  }[p.state] || { label: "Sin confirmar", cls: "bg-[#a3a3a3]/15 text-[#a3a3a3]", Icon: Clock };
                  const Badge = badge.Icon;
                  return (
                    <div key={p.id} className="nk-card p-4 mb-2 opacity-80" data-testid={`wallet-past-${p.id}`}>
                      <div className="flex justify-between text-sm gap-2">
                        <span className="text-[#a3a3a3]">{p.service_name} · {p.barber_name}</span>
                        <span className="text-[#737373] whitespace-nowrap">{fmtDate(p.start)} {fmtTime12(p.start)}</span>
                      </div>
                      <span className={`inline-flex items-center gap-1 mt-2 text-xs font-semibold px-2 py-1 rounded-full ${badge.cls}`} data-testid={`wallet-past-state-${p.id}`}><Badge className="w-3 h-3" />{badge.label}</span>
                    </div>
                  );
                })}
              </div>
            )}

            <div>
              <h3 className="font-bold mb-2">Historial</h3>
              {data.history.length === 0 ? <p className="text-[#737373] text-sm">Aún no hay visitas registradas.</p> :
                data.history.map((h) => (
                  <div key={h.id} className="flex justify-between text-sm py-2 border-b border-[#2a2a2a]">
                    <span>{h.service_name || h.kind}</span>
                    <span className="text-[#a3a3a3]">{fmtDate(h.created_at)} · {money(h.total)}</span>
                  </div>
                ))}
            </div>
            <Link to="/mi-cuenta/referir" className="nk-btn-ghost w-full flex items-center justify-center gap-2"><Gift className="w-4 h-4" />Invita y gana puntos</Link>
          </div>
        )}
      </div>
    </div>
  );
}
